import React, { useState, useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../../../store";
import type {
  WeaponMount,
  Weapon,
  WeaponRange,
} from "../../../../types/weapons";
import { WEAPON_TYPES, AMMO_TYPES } from "../../../../constants/ship";
import { POINT_DEFENCE_WEAPONS, SCREEN_SYSTEMS } from "../../../../constants/defensiveSystems";
import { updateWeaponMount, addWeaponMount } from "../../../../store/features/shipBuilderSlice";
import { formatCurrency } from "../../../../utils/formatting";
import { calculateSpinalWeaponStats, validateSpinalWeaponInstallation } from "../../../../utils/calculations/weaponCalculations";
import { selectors } from "../../../../store/selectors/shipBuilderSelectors";
import { 
  usesFirmpoints,
  getFirmpointRangeLimit,
  calculateFirmpointPowerReduction,
  canUpgradeMountToTurret,
  getMountConfig
} from "../../../../utils/weaponMountCalculations";
import { calculateWeaponTonnage, calculateMountWeaponTonnage } from "../../../../utils/weaponTonnageCalculations";

interface WeaponSelectorProps {
  mountIndex: number;
  mount: WeaponMount;
  removeMount?: () => void;
  upgradeToTurret?: () => void;
  canUpgradeToTurret?: boolean;
}

const WeaponSelector: React.FC<WeaponSelectorProps> = ({
  mountIndex,
  mount,
  removeMount,
  upgradeToTurret,
  canUpgradeToTurret,
}) => {
  const dispatch = useAppDispatch();
  const shipTechLevel = useAppSelector((state) => state.shipBuilder.techLevel);
  const shipSize = useAppSelector((state) => state.shipBuilder.hull?.size || 100);
  const weightOverrideActive = useAppSelector(selectors.selectOverrideWeight);
  const weaponMounts = useAppSelector((state) => state.shipBuilder.weaponMounts) || [];

  // Get available weapons based on mount type and tech level
  const getAvailableWeapons = () => {
    // Handle point defence weapons for point defence battery mounts
    if (mount.type === "pointDefenseBattery") {
      return Object.entries(POINT_DEFENCE_WEAPONS).filter(([id, weapon]) => {
        // Check tech level requirement
        return weapon.techLevel <= shipTechLevel;
      });
    }

    // Handle screen systems for screen mounts
    if (mount.type === "screen") {
      return Object.entries(SCREEN_SYSTEMS).filter(([id, system]) => {
        // Check tech level requirement
        return system.techLevel <= shipTechLevel;
      });
    }

    // Handle regular weapons for other mount types
    return Object.entries(WEAPON_TYPES).filter(([id, weapon]) => {
      // Check tech level requirement
      if (weapon.techLevel > shipTechLevel) {
        return false;
      }

      // Check mount type compatibility
      switch (mount.type) {
        case "turret":
          // Turrets can mount turret weapons
          return weapon.mountType === "turret";
        case "barbette":
          // Barbettes can mount barbette weapons
          return weapon.mountType === "barbette";
        case "smallBay":
          // Small bays can mount small bay weapons
          return weapon.mountType === "smallBay";
        case "mediumBay":
          // Medium bays can mount medium bay weapons
          return weapon.mountType === "mediumBay";
        case "largeBay":
          // Large bays can mount large bay weapons
          return weapon.mountType === "largeBay";
        case "spinalMount":
          // Spinal mounts can mount spinal weapons
          return weapon.mountType === "spinalMount";
        case "pointDefense":
          // Point defense mounts are for defensive weapons
          return weapon.traits.includes("Point Defense");
        case "fixed":
          // Fixed mounts can mount turret weapons (same as turrets)
          // According to Traveller rules: "Turrets and fixed mounts use the same type of weapons"
          return weapon.mountType === "turret";
        default:
          return true;
      }
    });
  };

  const availableWeapons = getAvailableWeapons();
  const [selectedWeaponType, setSelectedWeaponType] = useState<string>("");
  const [selectedAmmoType, setSelectedAmmoType] = useState<string>("");
  const [mountQuantity, setMountQuantity] = useState<number>(mount.quantity || 1);


  // Update local mount quantity when mount quantity changes
  useEffect(() => {
    setMountQuantity(mount.quantity || 1);
  }, [mount.quantity]);

  // Check if mount is at weapon capacity
  const isAtCapacity = () => {
    // For turrets, allow up to 3 weapons (triple turret)
    if (mount.type === 'turret') {
      return mount.weapons.length >= 3;
    }
    
    // For other mount types, use their specific max weapons
    const maxWeapons = mount.maxWeapons || 1;
    return mount.weapons.length >= maxWeapons;
  };


  // Calculate available hardpoints/firmpoints for mount duplication
  const getAvailableHardpoints = () => {
    const totalHardpoints = Math.floor(shipSize / 100);
    const usedHardpoints = weaponMounts.reduce((total, m) => total + (m.hardpointsUsed || 0), 0);
    return Math.max(0, totalHardpoints - usedHardpoints);
  };

  const getAvailableFirmpoints = () => {
    if (shipSize >= 100) return 0; // Ships 100+ tons use hardpoints
    const totalFirmpoints = shipSize < 35 ? 1 : shipSize < 70 ? 2 : 3;
    const usedFirmpoints = weaponMounts.reduce((total, m) => total + (m.firmpointsUsed || 0), 0);
    return Math.max(0, totalFirmpoints - usedFirmpoints);
  };

  // Calculate maximum mount quantity based on available hardpoints/firmpoints
  const getMaxMountQuantity = () => {
    const availableHardpoints = getAvailableHardpoints();
    const availableFirmpoints = getAvailableFirmpoints();
    const hardpointsPerMount = mount.hardpointsUsed || 0;
    const firmpointsPerMount = mount.firmpointsUsed || 0;

    if (shipSize >= 100) {
      // Large ships use hardpoints
      return hardpointsPerMount > 0 ? Math.floor(availableHardpoints / hardpointsPerMount) : 50;
    } else {
      // Small craft use firmpoints
      return firmpointsPerMount > 0 ? Math.floor(availableFirmpoints / firmpointsPerMount) : 50;
    }
  };

  // Function to handle mount quantity changes
  const handleMountQuantityChange = (newQuantity: number) => {
    const maxQuantity = getMaxMountQuantity();
    const actualQuantity = Math.min(Math.max(1, newQuantity), maxQuantity);
    
    // Update the mount's quantity property
    const updatedMount: WeaponMount = {
      ...mount,
      quantity: actualQuantity,
    };
    
    dispatch(updateWeaponMount({ index: mountIndex, mount: updatedMount }));
    setMountQuantity(actualQuantity); // Update local state
  };

  // Calculate damage with stacking rules
  const calculateMountDamage = () => {
    if (mount.weapons.length === 0) return "No weapons";

    // Group weapons by type for stacking
    const weaponGroups = mount.weapons.reduce(
      (groups, weapon) => {
        const key = weapon.name;
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(weapon);
        return groups;
      },
      {} as Record<string, Weapon[]>,
    );

    // Calculate damage for each weapon type
    const damageStrings = Object.entries(weaponGroups).map(
      ([weaponName, weapons]) => {
        const baseWeapon = weapons[0];
        const baseDamage = baseWeapon.damage;
        const additionalWeapons = weapons.length - 1;

        if (additionalWeapons === 0) {
          // Single weapon - check for damage multiple
          const damageMultiple = baseWeapon.damageMultiple || 1;
          if (damageMultiple > 1) {
            return `${weaponName}: ${baseDamage} × ${damageMultiple}`;
          }
          return `${weaponName}: ${baseDamage}`;
        } else {
          // Multiple weapons - apply stacking rules
          const damageMatch = baseDamage.match(/(\d+)D/);
          if (damageMatch) {
            const diceCount = parseInt(damageMatch[1]);
            const bonus = additionalWeapons * diceCount;

            // Check for damage multiple
            const damageMultiple = baseWeapon.damageMultiple || 1;
            if (damageMultiple > 1) {
              return `${weaponName}: (${baseDamage}+${bonus}) × ${damageMultiple} (${weapons.length} weapons)`;
            }
            return `${weaponName}: ${baseDamage}+${bonus} (${weapons.length} weapons)`;
          } else {
            const damageMultiple = baseWeapon.damageMultiple || 1;
            if (damageMultiple > 1) {
              return `${weaponName}: ${baseDamage} × ${damageMultiple} (${weapons.length} weapons)`;
            }
            return `${weaponName}: ${baseDamage} (${weapons.length} weapons)`;
          }
        }
      },
    );

    return damageStrings.join(", ");
  };

  const handleWeaponSelect = (weaponType: string) => {
    if (!weaponType || isAtCapacity()) return;

    // Handle point defence weapons, screen systems vs regular weapons
    let weaponData: any;
    let isDefensiveSystem = false;
    
    if (mount.type === "pointDefenseBattery") {
      weaponData = POINT_DEFENCE_WEAPONS[weaponType as keyof typeof POINT_DEFENCE_WEAPONS];
      isDefensiveSystem = true;
    } else if (mount.type === "screen") {
      weaponData = SCREEN_SYSTEMS[weaponType as keyof typeof SCREEN_SYSTEMS];
      isDefensiveSystem = true;
    } else {
      weaponData = WEAPON_TYPES[weaponType as keyof typeof WEAPON_TYPES];
    }
    
    if (!weaponData) return;

    // Validate spinal weapon installation if applicable
    if (mount.type === "spinalMount") {
      const validationErrors = validateSpinalWeaponInstallation(
        weaponData,
        shipSize,
        1,
        weightOverrideActive
      );
      if (validationErrors.length > 0) {
        alert(`Cannot install spinal weapon: ${validationErrors.join(", ")}`);
        return;
      }
    }

    // If weapon uses ammo, get the first available ammo type as default
    let ammoType = undefined;
    let ammoInfo = undefined;
    if ((weaponData as any).ammo) {
      // Find the first available ammo type for this weapon
      const availableAmmoTypes = Object.entries(AMMO_TYPES).filter(([, ammo]) => {
        const weaponAmmo = (weaponData as any).ammo;
        return weaponAmmo && ammo.type === weaponAmmo.type;
      });
      
      if (availableAmmoTypes.length > 0) {
        ammoType = availableAmmoTypes[0][0];
        ammoInfo = availableAmmoTypes[0][1];
      } else {
        return; // No ammo types available
      }
    }

    // Handle defensive systems vs weapons
    let newWeapon: Weapon;
    
    if (isDefensiveSystem) {
      // Handle point defence weapons and screen systems
      if (mount.type === "pointDefenseBattery") {
        // Point defence weapons have ammo (for gauss) and intercept scores
        newWeapon = {
          name: weaponData.name,
          description: weaponData.description,
          tl: weaponData.techLevel,
          range: weaponData.range,
          power: weaponData.power,
          damage: weaponData.damage,
          cost: weaponData.cost * 1000000, // Convert MCr to Cr
          tons: weaponData.weight,
          traits: weaponData.traits,
          mountType: weaponData.mountType,
          damageMultiple: undefined,
          attackModifier: undefined,
          baseSize: undefined,
          maxSize: undefined,
          // Handle ammo for gauss batteries
          ammo: weaponData.ammo ? {
            type: weaponData.ammo.type,
            capacity: weaponData.ammo.capacity,
            tonsPerReload: weaponData.ammo.tonsPerReload,
            costPerReload: weaponData.ammo.costPerReload,
            name: weaponData.ammo.name,
            traits: weaponData.ammo.traits,
            damage: weaponData.ammo.damage || "0",
            perReload: weaponData.ammo.perReload || 1,
            tl: weaponData.techLevel,
          } : undefined,
          currentAmmo: weaponData.ammo ? weaponData.ammo.capacity : undefined,
          selectedAmmoType: weaponData.ammo ? weaponData.ammo.type : undefined,
          // Point defence specific properties
          intercept: weaponData.intercept,
        };
      } else {
        // Screen systems don't have ammo or damage
        newWeapon = {
          name: weaponData.name,
          description: weaponData.description || weaponData.effect,
          tl: weaponData.techLevel,
          range: weaponData.range,
          power: weaponData.power,
          damage: weaponData.damage,
          cost: weaponData.cost * 1000000, // Convert MCr to Cr
          tons: weaponData.weight,
          traits: weaponData.traits,
          mountType: weaponData.mountType,
          damageMultiple: undefined,
          attackModifier: undefined,
          baseSize: undefined,
          maxSize: undefined,
          ammo: undefined, // Screen systems don't use ammo
          currentAmmo: undefined,
          selectedAmmoType: undefined,
          // Screen system specific properties
          effect: weaponData.effect,
        };
      }
    } else {
      // Handle regular weapons
      // Apply Firmpoint modifications if this is a fixed mount on a small craft
      let modifiedRange = weaponData.range as WeaponRange;
      let modifiedPower = weaponData.power;
      
      const shipUsesFirmpointsForShip = usesFirmpoints(shipSize);
      if (shipUsesFirmpointsForShip && mount.type === "fixed") {
        // Apply range limitations for weapons on Firmpoints
        modifiedRange = getFirmpointRangeLimit(modifiedRange, weaponData.traits) as WeaponRange;
        
        // Apply power reduction for weapons on Firmpoints (25% reduction, rounding up)
        modifiedPower = calculateFirmpointPowerReduction(modifiedPower);
      }

      // Calculate weapon tonnage based on Traveller rules and exceptions
      const weaponTons = calculateWeaponTonnage({
        weaponName: weaponData.name,
        mountType: mount.type,
        shipSize: shipSize,
      });
      
      newWeapon = {
        name: weaponData.name,
        description: weaponData.description,
        tl: weaponData.techLevel,
        range: modifiedRange,
        power: modifiedPower,
        damage: weaponData.damage,
        cost: weaponData.cost * 1000000, // Convert MCr to Cr
        tons: weaponTons, // Tonnage based on Traveller rules and exceptions
        traits: weaponData.traits,
        mountType: weaponData.mountType,
        damageMultiple: (weaponData as any).damageMultiple,
        attackModifier: (weaponData as any).attackModifier,
        baseSize: (weaponData as any).baseSize,
        maxSize: (weaponData as any).maxSize,
        ammo: ammoInfo
          ? {
              type: ammoInfo.type,
              capacity: (weaponData as any).ammo.capacity,
              tonsPerReload: ammoInfo.tonsPerReload,
              costPerReload: ammoInfo.cost,
              name: ammoInfo.name,
              traits: ammoInfo.traits,
              damage: ammoInfo.damage,
              perReload: ammoInfo.perReload,
              tl: ammoInfo.tl,
            }
          : (weaponData as any).ammo,
        currentAmmo: ammoInfo ? (weaponData as any).ammo.capacity : undefined,
        selectedAmmoType: ammoType,
      };
    }

    // Create single weapon (quantity selector removed)
    const weaponsToAdd: Weapon[] = [{ ...newWeapon }];

    // For spinal weapons, calculate proper stats including hardpoints
    let updatedMount: WeaponMount;
    if (mount.type === "spinalMount") {
      const spinalStats = calculateSpinalWeaponStats(newWeapon, 1); // Default to 1x size
      updatedMount = {
        ...mount,
        weapons: [...mount.weapons, ...weaponsToAdd],
        cost: spinalStats.cost,
        tons: spinalStats.tons,
        power: spinalStats.power,
        hardpointsUsed: spinalStats.hardpoints,
      };
    } else {
      // Standard weapon mount calculation
      // For turrets, automatically determine turret type based on number of weapons
      const newWeapons = [...mount.weapons, ...weaponsToAdd];
      let turretType = mount.turretType;
      
      if (mount.type === 'turret') {
        // Automatically determine turret type based on weapon count
        if (newWeapons.length === 1) {
          turretType = 'single';
        } else if (newWeapons.length === 2) {
          turretType = 'double';
        } else if (newWeapons.length === 3) {
          turretType = 'triple';
        }
      }
      
      // Get mount config with updated turret type
      const mountConfig = getMountConfig(mount.type, turretType);
      const totalWeaponTons = calculateMountWeaponTonnage(
        newWeapons,
        mount.type,
        shipSize
      );
      
      // Calculate new mount cost and tonnage
      const baseMountCost = mountConfig.cost;
      const baseMountTons = mountConfig.tons;
      const totalWeaponCost = newWeapons.reduce((sum, weapon) => sum + weapon.cost, 0);
      const totalWeaponPower = newWeapons.reduce((sum, weapon) => sum + weapon.power, 0);
      
      updatedMount = {
        ...mount,
        turretType, // Update turret type
        weapons: newWeapons,
        cost: baseMountCost + totalWeaponCost, // Base mount cost + weapon costs
        tons: baseMountTons + totalWeaponTons, // Base mount tonnage + weapon tonnage (with exceptions)
        power: totalWeaponPower, // Total power from all weapons
        maxWeapons: mountConfig.maxWeapons, // Update max weapons based on turret type
      };
    }

    dispatch(updateWeaponMount({ index: mountIndex, mount: updatedMount }));
    setSelectedWeaponType("");
    setSelectedAmmoType("");
  };

  const removeWeapon = (weaponIndex: number) => {
    const weaponToRemove = mount.weapons[weaponIndex];
    
    // For spinal weapons, reset the mount to empty state
    let updatedMount: WeaponMount;
    if (mount.type === "spinalMount") {
      updatedMount = {
        ...mount,
        weapons: [],
        cost: 0,
        tons: 0,
        power: 0,
        hardpointsUsed: 0,
      };
    } else {
      // Standard weapon mount calculation
      // Calculate total tonnage: mount tonnage + remaining weapon tonnage (based on Traveller rules)
      const remainingWeapons = mount.weapons.filter((_, index) => index !== weaponIndex);
      
      // For turrets, automatically determine turret type based on remaining weapon count
      let turretType = mount.turretType;
      
      if (mount.type === 'turret') {
        // Automatically determine turret type based on remaining weapon count
        if (remainingWeapons.length === 1) {
          turretType = 'single';
        } else if (remainingWeapons.length === 2) {
          turretType = 'double';
        } else if (remainingWeapons.length === 3) {
          turretType = 'triple';
        }
      }
      
      // Get mount config with updated turret type
      const mountConfig = getMountConfig(mount.type, turretType);
      const totalWeaponTons = calculateMountWeaponTonnage(
        remainingWeapons,
        mount.type,
        shipSize
      );
      
      // Calculate new mount cost and tonnage
      const baseMountCost = mountConfig.cost;
      const baseMountTons = mountConfig.tons;
      const totalWeaponCost = remainingWeapons.reduce((sum, weapon) => sum + weapon.cost, 0);
      const totalWeaponPower = remainingWeapons.reduce((sum, weapon) => sum + weapon.power, 0);
      
      updatedMount = {
        ...mount,
        turretType, // Update turret type
        weapons: remainingWeapons,
        cost: baseMountCost + totalWeaponCost, // Base mount cost + weapon costs
        tons: baseMountTons + totalWeaponTons, // Base mount tonnage + weapon tonnage (with exceptions)
        power: totalWeaponPower, // Total power from all weapons
        maxWeapons: mountConfig.maxWeapons, // Update max weapons based on turret type
      };
    }
    
    dispatch(updateWeaponMount({ index: mountIndex, mount: updatedMount }));
  };

  const formatCost = (cost: number) => {
    return formatCurrency(cost);
  };

  const getMountTypeDisplay = () => {
    const typeMap: Record<string, string> = {
      turret: mount.turretType ? `${mount.turretType.charAt(0).toUpperCase() + mount.turretType.slice(1)} Turret` : "Turret",
      barbette: "Barbette",
      smallBay: "Small Bay",
      mediumBay: "Medium Bay",
      largeBay: "Large Bay",
      spinalMount: "Spinal Mount",
      pointDefense: "Point Defense",
      fixed: "Fixed Mount",
      pointDefenseBattery: "Point Defence Battery",
      screen: "Screen",
    };
    const baseType = typeMap[mount.type] || mount.type;
    const quantity = mount.quantity || 1;
    return quantity > 1 ? `${quantity}x ${baseType}` : baseType;
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
          {getMountTypeDisplay()} Mount
        </h3>
        {removeMount && (
          <button
            className="px-3 py-1 text-sm border border-red-600 text-red-600 rounded hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
            onClick={removeMount}
          >
            Remove Mount
          </button>
        )}
      </div>







      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Mount Details
          </h4>
          <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
            <div>
              <span className="font-medium">Type:</span> {getMountTypeDisplay()}
            </div>
            <div>
              <span className="font-medium">Cost:</span>{" "}
              {formatCost((mount.cost || 0) * (mount.quantity || 1))}
              {(mount.quantity || 1) > 1 && (
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                  ({formatCost(mount.cost || 0)} each)
                </span>
              )}
            </div>
            <div>
              <span className="font-medium">Weight:</span> {(mount.tons || 0) * (mount.quantity || 1)} tons
              {(mount.quantity || 1) > 1 && (
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                  ({mount.tons || 0} tons each)
                </span>
              )}
            </div>
            <div>
              <span className="font-medium">Power:</span> {(mount.power || 0) * (mount.quantity || 1)}
              {(mount.quantity || 1) > 1 && (
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                  ({mount.power || 0} each)
                </span>
              )}
            </div>
            <div>
              <span className="font-medium">Hardpoints Used:</span>{" "}
              {(mount.hardpointsUsed || 0) * (mount.quantity || 1)}
              {(mount.quantity || 1) > 1 && (
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                  ({mount.hardpointsUsed || 0} each)
                </span>
              )}
            </div>
            <div>
              <span className="font-medium">
                {mount.type === "pointDefenseBattery" || mount.type === "screen" ? "Systems:" : "Weapons:"}
              </span>{" "}
              {mount.weapons.length} / {mount.maxWeapons || 1}
            </div>
            <div>
              <span className="font-medium">Gunners Required:</span> 1
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {mount.type === "pointDefenseBattery" 
              ? "Add Point Defence Weapon" 
              : mount.type === "screen" 
              ? "Add Screen System" 
              : "Add Weapon"
            }
          </h4>
          <div className="space-y-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-400 mb-1">
                {mount.type === "pointDefenseBattery" 
                  ? "Select Point Defence Weapon" 
                  : mount.type === "screen" 
                  ? "Select Screen System" 
                  : "Select Weapon"
                }
              </label>
              <select
                value={selectedWeaponType}
                onChange={(e) => {
                  const weaponType = e.target.value;
                  setSelectedWeaponType(weaponType);
                  if (weaponType) {
                    handleWeaponSelect(weaponType);
                  }
                }}
                disabled={isAtCapacity()}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="">
                  {mount.type === "pointDefenseBattery" 
                    ? "Choose a point defence weapon..." 
                    : mount.type === "screen" 
                    ? "Choose a screen system..." 
                    : "Choose a weapon..."
                  }
                </option>
                {availableWeapons.map(([id, weapon]) => (
                  <option key={id} value={id}>
                    {weapon.name} (TL{weapon.techLevel})
                  </option>
                ))}
              </select>
            </div>


            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {mount.type === "pointDefenseBattery" 
                ? "Select a point defence weapon to automatically add it to this battery. Gauss batteries require ammunition."
                : mount.type === "screen" 
                ? "Select a screen system to automatically add it to this mount."
                : "Select a weapon to automatically add it to this mount. Ammo types are automatically selected when applicable."
              }
            </p>
          </div>
        </div>
      </div>

      {/* Mount Quantity Section */}
      <div className="border-t border-gray-200 dark:border-gray-600 my-4 pt-4">
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Mount Quantity
          </h4>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => handleMountQuantityChange(mountQuantity - 1)}
              disabled={mountQuantity <= 1}
              className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-100"
            >
              −
            </button>
            <input
              type="number"
              min="1"
              max={getMaxMountQuantity()}
              value={mountQuantity}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 1;
                handleMountQuantityChange(value);
              }}
              className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
            />
            <button
              type="button"
              onClick={() => handleMountQuantityChange(mountQuantity + 1)}
              disabled={mountQuantity >= getMaxMountQuantity()}
              className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-100"
            >
              +
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Max: {getMaxMountQuantity()} mounts
          </p>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          Change quantity to multiply this mount. Cost, weight, power, and hardpoints will be calculated automatically. Limited by available hardpoints/firmpoints.
        </p>
      </div>

      {mount.weapons.length > 0 && (
        <>
          <div className="border-t border-gray-200 dark:border-gray-600 my-4"></div>
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Installed Weapons ({mount.weapons.length})
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              <span className="font-medium">Combined Damage:</span>{" "}
              {calculateMountDamage()}
            </p>
            <div className="space-y-3">
              {mount.weapons.map((weapon, index) => (
                <div
                  key={`${weapon.name}-${index}`}
                  className="flex flex-wrap items-center gap-2"
                >
                  <div className="inline-flex items-center gap-2 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 px-3 py-1 rounded-full text-sm">
                    <span>
                      {weapon.name} ({weapon.damage})
                    </span>
                    <button
                      onClick={() => removeWeapon(index)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 focus:outline-none"
                    >
                      ×
                    </button>
                  </div>

                  {weapon.ammo && (
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Ammo: {weapon.currentAmmo} / {weapon.ammo.capacity}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Firmpoint Upgrade Button */}
      {mount.type === "fixed" && canUpgradeToTurret && upgradeToTurret && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
          <button
            onClick={upgradeToTurret}
            className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors text-sm"
          >
            Upgrade Firmpoint to Turret (+MCr0.2, +1 ton)
          </button>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
            Allows firing in all directions. Only one Firmpoint can be upgraded per ship.
          </p>
        </div>
      )}
    </div>
  );
};

export default WeaponSelector;
